/**
 * Storage abstraction. On the web, uses localStorage. When Plan 5 wraps the app in
 * Capacitor, this swaps to @capacitor/preferences without changing call sites.
 *
 * Keys are namespaced under "509b/" to avoid collisions if hosted alongside other apps
 * on the same domain.
 */

const NAMESPACE = '509b/';

function ns(key: string): string {
  return NAMESPACE + key;
}

export function get<T>(key: string): T | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(ns(key));
    if (raw === null) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export function set<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ns(key), JSON.stringify(value));
  } catch {
    // Storage may be full or disabled (private window); silently ignore — UI shows current state, persistence is best-effort.
  }
}

export function remove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(ns(key));
  } catch {
    // ignore
  }
}

/** Clear all Truebend keys without touching unrelated localStorage on the same origin. */
export function clearAll(): void {
  for (const key of listKeys()) {
    remove(key);
  }
}

/** List all keys currently stored under our namespace (without prefix). */
export function listKeys(): string[] {
  if (typeof window === 'undefined') return [];
  const out: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const raw = window.localStorage.key(i);
    if (raw && raw.startsWith(NAMESPACE)) {
      out.push(raw.slice(NAMESPACE.length));
    }
  }
  return out;
}
